import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import SortieLocations from '@/models/finestHour/SortieLocations';
import Pilot from '@/models/Pilot';
import Countries from '@/models/Countries';

export default class Sortie {
  constructor({
    version = 1,
    date,
    rafStrength,
    missionType,
    bomberCount,
    luftwaffeFighterCount,
    damagedAircrafts = 0,
    destroyedAircrafts = 0,
    injuredPilots = [],
    killedPilots = [],
    capturedPilots = [],
    lostPilots = [],
    activeSlotsIDs = [],
    otherDailyEvents = '–',
    otherDailyEventsResult = '–',
    location = SortieLocations.overTheSea,
    notes = null,
    bomberType,
    escortType,
    reconnaissanceType,
    reinforcementType,
    killsByPilot = [],
    aircraftsDestroyedByPilot = [],
    aircraftsDamagedByPilot = [],
    log = [],
    germanSections = [],
    reinforcementsPossible = false,
    reinforcementsStatus = null,
    reinforcementsArrived = false,
    germanReinforcementSections = [],
  }) {
    this.version = version;
    this.date = moment(date);
    this.rafStrength = rafStrength;
    this.missionType = missionType;
    this.bomberCount = bomberCount;
    this.luftwaffeFighterCount = luftwaffeFighterCount;
    this.damagedAircrafts = damagedAircrafts;
    this.destroyedAircrafts = destroyedAircrafts;
    this.injuredPilots = injuredPilots;
    this.killedPilots = killedPilots;
    this.capturedPilots = capturedPilots;
    this.lostPilots = lostPilots;
    this.activeSlotsIDs = activeSlotsIDs;
    this.otherDailyEvents = otherDailyEvents;
    this.otherDailyEventsResult = otherDailyEventsResult;
    this.location = location;
    this.notes = notes;
    this.bomberType = bomberType;
    this.escortType = escortType;
    this.reconnaissanceType = reconnaissanceType;
    this.reinforcementType = reinforcementType;
    this.killsByPilot = killsByPilot;
    this.aircraftsDestroyedByPilot = aircraftsDestroyedByPilot;
    this.aircraftsDamagedByPilot = aircraftsDamagedByPilot;
    this.log = log;
    this.germanSections = germanSections.map((x) => ({
      ...x,
      country: (x.country instanceof Object)
        ? x.country
        : Countries.find((c) => c.name === x.country),
      pilots: x.pilots.map((p) => Pilot.fromJSON(p)),
    }));
    this.reinforcementsPossible = reinforcementsPossible;
    this.reinforcementsStatus = reinforcementsStatus;
    this.reinforcementsArrived = reinforcementsArrived;
    this.germanReinforcementSections = germanReinforcementSections.map((x) => ({
      ...x,
      country: (x.country instanceof Object)
        ? x.country
        : Countries.find((c) => c.name === x.country),
      pilots: x.pilots.map((p) => Pilot.fromJSON(p)),
    }));
  }

  get description() {
    switch (this.missionType) {
      case 'bounced by enemy fighters':
        return `${this.rafStrength} is bounced by ${this.luftwaffeFighterCount.toLowerCase()} of ${this.escortType}`;
      case 'about to bounce an enemy formation':
        return `${this.rafStrength} is about to bounce ${this.luftwaffeFighterCount.toLowerCase()} ${this.escortType}`;
      case 'vectored onto reconnaissance aircraft':
        return `${this.rafStrength} is vectored onto a ${this.reconnaissanceType} reconnaissance aircraft`;
      case 'scrambled to intercept':
      case 'scrambled to intercept enemy raid':
        return this.luftwaffeFighterCount === 'None'
          ? `${this.rafStrength} is scrambled to intercept ${this.bomberCount} ${this.bomberType} bombers`
          : `${this.rafStrength} is scrambled to intercept ${this.bomberCount} ${this.bomberType} bombers escorted by a ${this.luftwaffeFighterCount.toLowerCase()} of ${this.escortType}`;
      case 'encounters unescorted bombers':
        return `${this.rafStrength} encounters ${this.bomberCount} unescorted ${this.bomberType} bombers`;
      default:
        break;
    }
    return '–';
  }

  addLogEntry(entry) {
    this.log.push({
      id: uuidv4(),
      description: entry,
    });
  }

  toPOJO() {
    const pojo = {
      ...this,
      date: this.date.format(),
      germanSections: this.germanSections.map((s) => ({
        ...s,
        country: s.country.name,
        pilots: s.pilots.map((p) => p.toJSON()),
      })),
      germanReinforcementSections: this.germanReinforcementSections.map((s) => ({
        ...s,
        country: s.country.name,
        pilots: s.pilots.map((p) => p.toJSON()),
      })),
    };
    return pojo;
  }

  static fromPOJO(pojo) {
    return new Sortie(pojo);
  }
}
